import React, { useEffect, useRef, useState } from "react";
import {
    IonPage,
    IonContent,
    IonButton,
    IonIcon,
    IonCard,
    IonCardContent
} from "@ionic/react";
import {
    callOutline,
    navigateOutline,
    alertCircleOutline,
} from "ionicons/icons";
import "./Emergency.css";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { usePlacesData } from "../hooks/usePlacesData";
import localPlaces from "../data/emergency.json";
import { medical, shield, paw, apps, medkit } from "ionicons/icons";
import { track } from "../analytics";

const centerDefault = { lat: 10.6765, lng: 122.9509 };

const getDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const callPlace = (phone: string[]) => {
    track("emergency_call", { numbers: phone.length });
    if (phone.length === 1) {
        // single number — call directly
        window.open(`tel:${phone[0]}`);
    } else {
        // multiple numbers — show action sheet
        const sheet = document.createElement("div");
        sheet.className = "call-sheet-backdrop";
        sheet.innerHTML = `
        <div class="call-sheet">
          <div class="call-sheet-title">Choose a number</div>
          ${phone.map((n) => `
            <a href="tel:${n}" class="call-sheet-option">${n}</a>
          `).join("")}
          <button class="call-sheet-cancel">Cancel</button>
        </div>
      `;
        sheet.querySelector(".call-sheet-cancel")!.addEventListener("click", () => sheet.remove());
        sheet.addEventListener("click", (e) => { if (e.target === sheet) sheet.remove(); });
        document.body.appendChild(sheet);
    }
};

const Emergency: React.FC = () => {
    const [userLocation, setUserLocation] = useState(centerDefault);
    const [filter, setFilter] = useState<"all" | "hospital" | "police" | "others" | "vet" | "pharmacy">("all");
    const [locationReady, setLocationReady] = useState(false);
    const [locationDenied, setLocationDenied] = useState(false);
    const { places: allPlaces, loading: placesLoading } = usePlacesData();
    const effectivePlaces = allPlaces.length > 0 ? allPlaces : localPlaces;
    const [places, setPlaces] = useState<any[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<Record<string, HTMLIonButtonElement | null>>({});

    useEffect(() => {
        if (effectivePlaces.length === 0) return;
        const filtered = effectivePlaces.filter(
            (p) => filter === "all" || p.type === filter
        );
        const sorted = [...filtered].sort((a, b) => {
            const d1 = getDistance(a.lat, a.lng, userLocation.lat, userLocation.lng);
            const d2 = getDistance(b.lat, b.lng, userLocation.lat, userLocation.lng);
            return d1 - d2;
        });
        setPlaces(sorted);
    }, [filter, userLocation, effectivePlaces]); // 👈 added effectivePlaces

    useEffect(() => {
        const getLocation = async () => {
            try {
                if (Capacitor.isNativePlatform()) {
                    const permission = await Geolocation.requestPermissions();
                    if (permission.location === "granted") {
                        const pos = await Geolocation.getCurrentPosition({
                            timeout: 10000,
                            enableHighAccuracy: false,
                        });
                        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                    } else {
                        setLocationDenied(true); // 👈 permission denied
                    }
                } else {
                    navigator.geolocation.getCurrentPosition(
                        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                        () => setLocationDenied(true), // 👈 permission denied
                        { timeout: 10000, enableHighAccuracy: false }
                    );
                }
            } catch (err) {
                console.warn("Location error:", err);
                setLocationDenied(true);
            } finally {
                setLocationReady(true);
            }
        };

        getLocation();
    }, []);

    // nearest cards always from full unfiltered list
    const nearestPolice = effectivePlaces
        .filter((p) => p.type === "police")
        .sort((a, b) =>
            getDistance(a.lat, a.lng, userLocation.lat, userLocation.lng) -
            getDistance(b.lat, b.lng, userLocation.lat, userLocation.lng)
        )[0];

    const nearestHospital = effectivePlaces
        .filter((p) => p.type === "hospital")
        .sort((a, b) =>
            getDistance(a.lat, a.lng, userLocation.lat, userLocation.lng) -
            getDistance(b.lat, b.lng, userLocation.lat, userLocation.lng)
        )[0];

    const navigate = (p: any) => {
        track("emergency_directions", { type: p.type, name: p.name });
        if (p.googleMapsUrl) {
            window.open(p.googleMapsUrl); // 👈 opens exact pinned location
        } else {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`);
        }
    };

    const filterConfig = {
        all: { icon: apps, label: "All" },
        hospital: { icon: medical, label: "Hospital" },
        police: { icon: shield, label: "Police" },
        vet: { icon: paw, label: "Vet" },
        pharmacy: { icon: medkit, label: "Pharmacy" },
        others: { icon: apps, label: "Others" },
    };

    // show loading only if no cached data yet
    if (placesLoading && effectivePlaces.length === 0) {
        return (
            <IonPage>
                <IonContent>
                    <div className="location-loading">Loading emergency data...</div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonContent className="emergency-content web-centered">

                {/* Hero */}
                <div className="emergency-hero">
                    <div className="emergency-hero-icon">
                        <IonIcon icon={alertCircleOutline} />
                    </div>
                    <div className="emergency-hero-text">
                        <span className="emergency-hero-kicker">Quick help</span>
                        <h1 className="emergency-hero-title">Emergency</h1>
                        <p className="emergency-hero-sub">
                            Nearest hospitals, police, pharmacies &amp; more — call or get
                            directions in one tap.
                        </p>
                    </div>
                </div>

                {/* TOP CARDS - always show nearest police and hospital */}
                <div className="emergency-section-head">
                    <span className="emergency-section-label">Priority actions</span>
                </div>
                <div className="top-cards">
                    {nearestPolice && (
                        <div className="action-card police" onClick={() => callPlace(nearestPolice.phone)}>
                            <div className="action-card-head">
                                <span className="action-card-emoji">🚓</span>
                                <span className="action-card-chip">
                                    <IonIcon icon={callOutline} />
                                    Call
                                </span>
                            </div>
                            <div className="action-card-label">Nearest Police</div>
                            <div className="action-card-name">{nearestPolice.name}</div>
                        </div>
                    )}
                    {nearestHospital && (
                        <div className="action-card hospital" onClick={() => navigate(nearestHospital)}>
                            <div className="action-card-head">
                                <span className="action-card-emoji">🏥</span>
                                <span className="action-card-chip">
                                    <IonIcon icon={navigateOutline} />
                                    Directions
                                </span>
                            </div>
                            <div className="action-card-label">Nearest Hospital</div>
                            <div className="action-card-name">{nearestHospital.name}</div>
                        </div>
                    )}
                </div>

                {/* FILTER BUTTONS */}
                <div className="emergency-section-head emergency-section-head-compact">
                    <span className="emergency-section-label">Browse nearby</span>
                </div>
                <div className="filters" ref={containerRef}>
                    {(["all", "hospital", "police", "vet", "pharmacy", "others"] as const).map((f) => (
                        <IonButton
                            key={f}
                            size="small"
                            ref={(el) => (buttonRefs.current[f] = el)}
                            className={`filter-btn ${filter === f ? "selected" : ""}`}
                            fill={filter === f ? "solid" : "outline"}
                            onClick={() => {
                                setFilter(f);

                                const btn = buttonRefs.current[f];
                                const container = containerRef.current;

                                if (btn && container) {
                                    container.scrollTo({
                                        left: btn.offsetLeft - 12, // aligns near left with padding
                                        behavior: "smooth",
                                    });
                                }
                            }}
                        >
                            <IonIcon icon={filterConfig[f].icon} slot="start" />
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </IonButton>
                    ))}
                </div>

                {/* LIST */}
                <div className="list">
                    {!locationReady ? (
                        <div className="location-loading">📍 Getting your location...</div>
                    ) : (
                        places.map((p) => {
                            const d = !locationDenied
                                ? getDistance(p.lat, p.lng, userLocation.lat, userLocation.lng)
                                : null;
                            return (
                                <IonCard key={p.id} className={p.type}>
                                    <IonCardContent>
                                        <div className="place-header">
                                            <div className="place-icon-shell">
                                                <span className="place-icon">
                                                    {
                                                        p.type === "hospital"
                                                            ? "🏥"
                                                            : p.type === "police"
                                                                ? "🚓"
                                                                : p.type === "vet"
                                                                    ? "🐾"
                                                                    : p.type === "pharmacy"
                                                                    ? "💊"
                                                                    : "🏛️"
                                                    }
                                                </span>
                                            </div>
                                            <div className="place-copy">
                                                <div className="place-meta">
                                                    <span className="place-type-chip">
                                                        {filterConfig[p.type as keyof typeof filterConfig]?.label ?? "Other"}
                                                    </span>
                                                    {d !== null && (
                                                        <span className="place-distance-chip">{d.toFixed(2)} km away</span>
                                                    )}
                                                </div>
                                                <strong className="place-name">{p.name}</strong>
                                                <p className="place-distance">
                                                    {p.address}
                                                </p>
                                                {/* ✅ NEW: Operating Hours */}
                                                {filter !== "all" && p.operatingHours && (
                                                <p className="place-hours">
                                                    {p.operatingHours}
                                                </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="place-actions">
                                            <IonButton size="small" onClick={() => callPlace(p.phone)} disabled={!p.phone || p.phone.length === 0}>
                                                <IonIcon slot="start" icon={callOutline} />
                                                {!p.phone || p.phone.length === 0 ? "Not Available" : "Call"}
                                            </IonButton>
                                            <IonButton size="small" fill="outline" onClick={() => navigate(p)}>
                                                <IonIcon slot="start" icon={navigateOutline} />
                                                Directions
                                            </IonButton>
                                        </div>
                                    </IonCardContent>
                                </IonCard>
                            );
                        })
                    )}
                </div>

            </IonContent>
        </IonPage>
    );
};

export default Emergency;
